// iOS-style search field + chip filter rail. The search field uses the
// iPadOS "subtle fill" treatment (12% tint, no border, rounded 10pt).
// The chip rail is a horizontally-scrollable row of pill filters, the
// same affordance as the Messages app tabs or App Store "Today" section
// scrubbing nav.

import { ITEM_KINDS, labelForKind, type SearchFilters } from "../data/catalog.ts";
import { SearchIcon } from "./icons.tsx";

export function SearchBar({
  filters,
  onChange,
}: {
  filters: SearchFilters;
  onChange: (next: SearchFilters) => void;
}) {
  const set = (patch: Partial<SearchFilters>) => onChange({ ...filters, ...patch, offset: 0 });

  return (
    <div className="px-4 md:px-6 pb-4 space-y-3">
      <label className="flex items-center gap-2 h-11 px-3.5 rounded-[10px] bg-slate-500/10">
        <SearchIcon className="text-slate-400 shrink-0" />
        <input
          type="search"
          placeholder="Search by name, SKU, or UPC"
          value={filters.q ?? ""}
          onChange={(e) => set({ q: e.target.value })}
          className="flex-1 bg-transparent text-[17px] placeholder:text-slate-400 focus:outline-none"
          aria-label="Search products"
        />
      </label>

      <div className="-mx-4 md:-mx-6 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 px-4 md:px-6 pb-1">
          <Chip active={!filters.kind} onClick={() => set({ kind: undefined })} label="All" />
          {ITEM_KINDS.map((k) => (
            <Chip
              key={k}
              active={filters.kind === k}
              onClick={() => set({ kind: k })}
              label={labelForKind(k)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Chip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap h-9 px-3.5 rounded-full text-[15px] font-medium shrink-0 transition-colors ${
        active
          ? "bg-slate-900 text-white"
          : "bg-slate-500/10 text-slate-700 hover:bg-slate-500/15 active:bg-slate-500/20"
      }`}
    >
      {label}
    </button>
  );
}
